import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, WarningCircle, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * `/candidate/magic/:token` — Zero-friction candidate activation landing.
 *
 * Recruiters add a candidate → drip email carries a signed magic link → the
 * candidate lands here → we POST the token to
 * `/api/auth/candidate/magic/consume` → backend auto-signs them in (creates
 * the user row if one doesn't exist yet) → we redirect to the candidate
 * dashboard.
 *
 * States:
 *   - "verifying"  — spinner while the token exchange is in flight
 *   - "expired"    — token expired / invalid / already used (offer resend)
 *   - "success"    — brief success state before redirect
 */
export default function CandidateMagicLanding() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [status, setStatus] = useState("verifying"); // verifying | expired | success | error
    const [message, setMessage] = useState("");
    const [errCode, setErrCode] = useState(""); // invalid | expired | used
    const [resendEmail, setResendEmail] = useState("");
    const [resending, setResending] = useState(false);
    const [resendDone, setResendDone] = useState(false);
    const attempted = useRef(false); // React 18 StrictMode double-invoke guard

    const requestFreshLink = async () => {
        const trimmed = (resendEmail || "").trim();
        if (!trimmed || !trimmed.includes("@")) {
            toast.error("Enter the email your recruiter used to contact you.");
            return;
        }
        setResending(true);
        try {
            const { data } = await api.post("/auth/candidate/send-login-link", {
                email: trimmed,
            });
            setResendDone(true);
            toast.success(data?.message || "Sign-in link sent — check your inbox.");
            if (data?.dev_magic_url) {
                // eslint-disable-next-line no-console
                console.info("[dev] magic link:", data.dev_magic_url);
            }
        } catch (ex) {
            toast.error(describeAxiosError(ex));
        } finally {
            setResending(false);
        }
    };

    useEffect(() => {
        if (attempted.current) return;
        attempted.current = true;

        (async () => {
            try {
                const { data } = await api.post("/auth/candidate/magic/consume", {
                    token: token || "",
                });
                setStatus("success");
                setMessage(
                    data.is_new_user
                        ? `Welcome, ${data.name?.split(" ")[0] || "there"} — signing you in…`
                        : `Welcome back, ${data.name?.split(" ")[0] || "there"}…`,
                );
                // Re-fetch /auth/me so `AuthProvider.user` reflects the new
                // cookie session before we navigate. Otherwise RoleGate on
                // /candidate sees stale `user = false` and bounces us back
                // to the login screen.
                try {
                    await refresh();
                } catch {
                    // fall through — worst case dashboard will re-fetch itself
                }
                // Short delay so the success state is visible, then hop into
                // the candidate dashboard. `has_password: false` triggers the
                // dashboard's "Set a password (optional)" banner on load.
                setTimeout(() => {
                    const dest = data.has_password
                        ? "/candidate"
                        : "/candidate?welcome=1";
                    navigate(dest, { replace: true });
                }, 900);
            } catch (ex) {
                const detail = ex?.response?.data?.detail;
                const code = detail && typeof detail === "object" ? detail.code : "";
                const msg = detail && typeof detail === "object" ? detail.message : describeAxiosError(ex);
                setErrCode(code || "invalid");
                setStatus(code === "expired" || code === "used" ? "expired" : "error");
                setMessage(msg || "This link is no longer valid.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, navigate]);

    return (
        <div
            data-testid="candidate-magic-landing"
            className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4"
        >
            <div className="w-full max-w-md bg-white border border-[#E5E5E5] p-8">
                <div className="mb-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                        NaukriDaddy · One-tap sign-in
                    </p>
                </div>

                {status === "verifying" && (
                    <div data-testid="magic-verifying" className="space-y-4">
                        <div className="w-10 h-10 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
                        <h1 className="text-2xl font-bold text-[#0A0A0A] leading-tight">
                            Opening your profile…
                        </h1>
                        <p className="text-sm text-[#525252]">
                            Verifying your invite link and signing you in. This
                            takes a moment.
                        </p>
                    </div>
                )}

                {status === "success" && (
                    <div data-testid="magic-success" className="space-y-4">
                        <CheckCircle size={36} weight="fill" className="text-[#059669]" />
                        <h1 className="text-2xl font-bold text-[#0A0A0A] leading-tight">
                            You&apos;re in.
                        </h1>
                        <p className="text-sm text-[#525252]">{message}</p>
                    </div>
                )}

                {(status === "expired" || status === "error") && (
                    <div data-testid="magic-expired" className="space-y-5">
                        <WarningCircle size={36} weight="fill" className="text-[#B45309]" />
                        <h1 className="text-2xl font-bold text-[#0A0A0A] leading-tight">
                            {errCode === "used"
                                ? "This link has already been used."
                                : "This link is no longer valid."}
                        </h1>
                        <p className="text-sm text-[#525252]">
                            {message}
                            {" "}
                            No problem — get a fresh sign-in link below.
                        </p>

                        {/* One-tap "send me a fresh link" (V1.2 P2) */}
                        <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-4 space-y-3">
                            <label
                                htmlFor="magic-resend-email"
                                className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] block"
                            >
                                Your email
                            </label>
                            <input
                                id="magic-resend-email"
                                type="email"
                                value={resendEmail}
                                onChange={(e) => setResendEmail(e.target.value)}
                                placeholder="you@example.com"
                                data-testid="magic-resend-email-input"
                                disabled={resendDone}
                                className="w-full bg-white border border-[#E5E5E5] px-3 py-2.5 text-sm text-[#0A0A0A] placeholder-[#A3A3A3] focus:outline-none focus:border-[#0A0A0A] disabled:opacity-60"
                            />
                            <button
                                type="button"
                                onClick={requestFreshLink}
                                disabled={resending || resendDone}
                                data-testid="magic-resend-btn"
                                className="w-full bg-[#0A0A0A] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 hover:bg-[#1F2937] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <EnvelopeSimple size={14} weight="bold" />
                                {resending
                                    ? "Sending…"
                                    : resendDone
                                        ? "Link sent — check your inbox"
                                        : "Email me a fresh sign-in link"}
                            </button>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <button
                                type="button"
                                data-testid="magic-signin-btn"
                                onClick={() => navigate("/candidate/login", { replace: true })}
                                className="flex-1 border border-[#E5E5E5] text-[#0A0A0A] px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 hover:bg-[#F5F5F5] transition-colors"
                            >
                                Sign in with password <ArrowRight size={14} weight="bold" />
                            </button>
                            <button
                                type="button"
                                data-testid="magic-signup-btn"
                                onClick={() => navigate("/candidate/signup", { replace: true })}
                                className="flex-1 border border-[#E5E5E5] text-[#0A0A0A] px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 hover:bg-[#F5F5F5] transition-colors"
                            >
                                Create account
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
